"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ROLLUP_OPTIONS = exports.ENUM_ROLLUP_OPTIONS = exports.DEFAULT_VIZ_LIMIT = void 0;
exports.getDefaultRollupFn = getDefaultRollupFn;
exports.getDimensionDisplayLabel = getDimensionDisplayLabel;
exports.getDimensions = getDimensions;
exports.getIsFastData = getIsFastData;
exports.getQueryDuration = getQueryDuration;
exports.getRollupKey = getRollupKey;
exports.getWindowFn = getWindowFn;
exports.isAggregateQuery = isAggregateQuery;
exports.isDirectionalDim = isDirectionalDim;
exports.isDstDim = isDstDim;
exports.isSrcDim = isSrcDim;
exports.sortResultRows = sortResultRows;
var _orderBy2 = _interopRequireDefault(require("lodash/orderBy"));
var _measurements = require("./measurements");
var _enumUtils = require("./enumUtils");
/**
 * Default number of rows whose time series are rendered when `viz.limit` is not set.
 */
const DEFAULT_VIZ_LIMIT = exports.DEFAULT_VIZ_LIMIT = 100;

/**
 * Available standard rollup functions for metric aggregation
 */
const ROLLUP_OPTIONS = exports.ROLLUP_OPTIONS = [{
  value: 'min',
  label: 'Min'
}, {
  value: 'avg',
  label: 'Average'
}, {
  value: 'max',
  label: 'Max'
}, {
  value: 'last',
  label: 'Last'
}, {
  value: 'sum',
  label: 'Total'
}, {
  value: 'p95',
  label: 'p95'
}];

/**
 * A subset of rollup options appropriate for enum values (they return valid values)
 */
const ENUM_ROLLUP_OPTIONS = exports.ENUM_ROLLUP_OPTIONS = [{
  value: 'min',
  label: 'Min'
}, {
  value: 'max',
  label: 'Max'
}, {
  value: 'last',
  label: 'Last'
}];

/**
 * Get the default rollup function for a metric based on its unit type.
 *
 * Different metric types have different semantics for aggregation:
 * - 'health': Use 'min' (lower = worse: 100=critical, 200=warning, 300=healthy)
 * - 'http_status_code': Use 'max' (higher = worse: 5xx > 4xx > 3xx > 2xx)
 * - 'dns_code': Use 'max' (higher codes are generally errors)
 * - default: Use 'avg'
 */
function getDefaultRollupFn(metricInfo) {
  if ((0, _enumUtils.isEnumItem)(metricInfo)) {
    return 'last';
  }
  if (!metricInfo?.Unit) {
    return 'avg';
  }
  const unit = metricInfo.Unit.split(':')[0];
  switch (unit) {
    case 'health':
      return 'min';
    case 'http_status_code':
    case 'dns_code':
      return 'max';
    case 'timestamp':
      return 'last';
    default:
      return 'avg';
  }
}
function getWindowFn(metricInfo, rollupFn) {
  if (metricInfo) {
    if (metricInfo.Window) {
      return metricInfo.Window;
    }
    // For SQL-compatible functions (min, max, avg, sum), use directly
    // For post-processing functions (p95, etc.), use 'avg' as SQL base
    const resolvedRollupFn = rollupFn || getDefaultRollupFn(metricInfo);
    if (['avg', 'min', 'max', 'sum'].includes(resolvedRollupFn)) {
      return resolvedRollupFn;
    }
  }
  return 'avg';
}
function getQueryDuration(query) {
  if (query.time.lookback) {
    return query.time.lookback;
  }
  return query.time.end - query.time.start;
}
function getIsFastData(query) {
  const duration = getQueryDuration(query);
  if (!query.window) {
    return false;
  }
  const hasSmallWindowOrDuration = query.window && query?.window.size > 0 && query?.window.size < 3600 || duration <= 60 * 60 * 24;
  if (hasSmallWindowOrDuration) {
    return false;
  }
  if (!query.measurementObj?.storage.SupportsFastData) {
    return false;
  }
  if (query.window && query.window.size >= 3600) {
    return true;
  }
  // doing this now until we properly calculate fast data retention
  return null;
}
function getDimensions(query) {
  return query.dimensions.length === 0 ? [{
    name: 'name'
  }] : query.dimensions;
}
function getRollupKey(rollup) {
  return `${rollup.fn}_${rollup.metric}`;
}

/**
 * Sort result rows by relevance, using the same precedence applied server-side:
 * explicit `query.sort` first, then aggregate metrics, then rollups. Pass
 * `isAggregate` from `isAggregateQuery` so aggregate queries sort by metric.
 */
function sortResultRows(query, rows, {
  isAggregate = false
} = {}) {
  if (query.sort?.length) {
    return (0, _orderBy2.default)(rows, query.sort.map(s => row => row.rollups[s.rollup] ?? 0), query.sort.map(s => s.order ?? 'desc'));
  }
  if (isAggregate) {
    return (0, _orderBy2.default)(rows, query.metrics.map(m => row => row.rollups[m.name] ?? 0), Array(query.metrics.length).fill('desc'));
  }
  const rollups = query.rollups ?? [];
  if (!rollups.length) {
    // No sort and no rollups to rank by — preserve the incoming order.
    return rows;
  }
  return (0, _orderBy2.default)(rows, rollups.map(rollup => {
    const key = getRollupKey(rollup);
    return row => row.rollups[key] ?? 0;
  }), Array(rollups.length).fill('desc'));
}
function isAggregateQuery(query) {
  const {
    measurementObj
  } = query;
  return !!(0, _measurements.getMeasurementMetric)(measurementObj, query.metrics[0].name)?.Aggregate;
}
function isSrcDim(dimensionName) {
  return dimensionName.startsWith('src_') || dimensionName.endsWith('_src') || dimensionName.includes('_src_') || dimensionName.startsWith('input_') || dimensionName.includes('_input_') || dimensionName.endsWith('_in');
}
function isDstDim(dimensionName) {
  return dimensionName.startsWith('dst_') || dimensionName.endsWith('_dst') || dimensionName.includes('_dst_') || dimensionName.startsWith('output_') || dimensionName.includes('_output_') || dimensionName.endsWith('_out');
}
function isDirectionalDim(dimensionName) {
  const hasSrc = isSrcDim(dimensionName);
  const hasDst = isDstDim(dimensionName);
  return hasSrc || hasDst;
}

/**
 * Get a display label for a dimension that includes the src/dst prefix when applicable.
 *
 * For dimensions like src_agent_asn or dst_agent_ip, the base Label in the measurement
 * definition is "Agent ASN" or "Agent IP" (without prefix) so the grouped dimension
 * selector UI can show SRC/DST toggle buttons. This function adds the prefix back
 * for contexts where we need to clearly distinguish which direction was selected
 * (e.g., legend headers, sidebar selected dimensions list).
 *
 * @param dimensionName - The dimension name (e.g., 'src_agent_asn', 'dst_agent_ip')
 * @param baseLabel - The base label from the dimension definition (e.g., 'Agent ASN')
 * @returns Display label with prefix if applicable (e.g., 'Src Agent ASN')
 */
function getDimensionDisplayLabel(dimensionName, baseLabel) {
  if (isSrcDim(dimensionName)) {
    return `Source ${baseLabel}`;
  }
  if (isDstDim(dimensionName)) {
    return `Destination ${baseLabel}`;
  }
  return baseLabel;
}